import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Camera, Send, Phone, Mail, User, AlertTriangle, CheckCircle, X, Loader2, ChevronDown, Image } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';
import PublicLogo from '../components/PublicLogo';
import PublicFeatureNav from '../components/PublicFeatureNav';
import AddressAutocomplete from '../components/AddressAutocomplete';

const API = process.env.REACT_APP_BACKEND_URL;

const SITUATION_OPTIONS = [
  { value: 'swarm_tree_branch', label: 'Swarm on tree or branch', icon: '🌳', free: true },
  { value: 'swarm_fence_post', label: 'Swarm on fence or post', icon: '🪵', free: true },
  { value: 'swarm_ground_level', label: 'Swarm at ground level', icon: '🌿', free: true },
  { value: 'swarm_elevated', label: 'Swarm high up (elevated)', icon: '⬆️', free: true },
  { value: 'swarm_building_exterior', label: 'Swarm on building exterior', icon: '🏠', free: true },
  { value: 'bees_in_wall', label: 'Bees entering a wall cavity', icon: '🧱', free: false },
  { value: 'bees_in_roof', label: 'Bees in roof or ceiling space', icon: '🏗️', free: false },
  { value: 'bees_in_chimney', label: 'Bees in chimney', icon: '🔥', free: false },
  { value: 'bees_in_ground', label: 'Bees in ground or retaining wall', icon: '⛏️', free: false },
  { value: 'established_colony', label: 'Established colony (been there weeks+)', icon: '🐝', free: false },
  { value: 'other', label: 'Other (please describe)', icon: '❓', free: true },
];

const HEIGHT_OPTIONS = ['Ground level', 'Under 1m', '1-2m', '2-3m', '3-5m', '5m+', 'Unsure'];

export default function ReportSwarm() {
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [reportId, setReportId] = useState(null);
  const fileInputRef = useRef(null);

  const [form, setForm] = useState({
    reporter_name: '',
    reporter_phone: '',
    reporter_email: '',
    address: '',
    suburb: '',
    state: '',
    postcode: '',
    latitude: 0,
    longitude: 0,
    situation_type: '',
    height_estimate: '',
    access_notes: '',
    already_arranged: false,
    last_seen: '',
    description: '',
  });
  const [photos, setPhotos] = useState([]);
  const [photoPreview, setPhotoPreview] = useState([]);
  const [locating, setLocating] = useState(false);

  const update = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const handlePhotos = (e) => {
    const files = Array.from(e.target.files || []).slice(0, 5 - photos.length);
    const newPhotos = [...photos, ...files].slice(0, 5);
    setPhotos(newPhotos);
    const previews = newPhotos.map(f => URL.createObjectURL(f));
    setPhotoPreview(previews);
  };

  const removePhoto = (idx) => {
    setPhotos(p => p.filter((_, i) => i !== idx));
    setPhotoPreview(p => p.filter((_, i) => i !== idx));
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) return toast.error('Geolocation not supported');
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        update('latitude', latitude);
        update('longitude', longitude);
        try {
          const res = await axios.get(`https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`);
          const addr = res.data?.display_name || '';
          const suburb = res.data?.address?.suburb || res.data?.address?.city || res.data?.address?.town || '';
          setForm(f => ({ ...f, address: addr, suburb, latitude, longitude }));
          toast.success('Location detected');
        } catch {
          setForm(f => ({ ...f, latitude, longitude }));
          toast.success('Coordinates captured — please enter address manually');
        }
        setLocating(false);
      },
      () => { setLocating(false); toast.error('Could not get location. Please enter address manually.'); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const handleSubmit = async () => {
    if (!form.reporter_name || !form.reporter_phone) return toast.error('Please enter your name and phone number');
    if (!form.situation_type) return toast.error('Please select the situation type');
    if (!form.address) return toast.error('Please provide an address');
    
    setSubmitting(true);
    try {
      // Auto-geocode if no GPS coordinates
      let lat = form.latitude;
      let lon = form.longitude;
      let suburb = form.suburb;
      if (!lat || lat === 0) {
        const fullAddr = [form.address, form.suburb, form.state, form.postcode].filter(Boolean).join(', ');
        try {
          const geo = await axios.get(`https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(fullAddr)}&format=json&limit=1`);
          if (geo.data?.[0]) {
            lat = parseFloat(geo.data[0].lat);
            lon = parseFloat(geo.data[0].lon);
            if (!suburb) suburb = form.suburb || geo.data[0].display_name?.split(',')[1]?.trim() || '';
          }
        } catch { /* geocoding failed, submit anyway */ }
      }

      const fd = new FormData();
      const submitData = { ...form, latitude: lat, longitude: lon, suburb: suburb || form.suburb };
      Object.entries(submitData).forEach(([k, v]) => fd.append(k, v));
      photos.forEach(p => fd.append('photos', p));
      
      const res = await axios.post(`${API}/api/swarm-reports/public`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setReportId(res.data.id);
      setSubmitted(true);
      toast.success('Swarm report submitted!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit report');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen honeycomb-bg flex items-center justify-center p-4">
        <Card className="max-w-lg w-full">
          <CardContent className="p-8 text-center">
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-stone-800 mb-2">Report Submitted</h2>
            <p className="text-stone-600 mb-4">
              Thank you! Nearby beekeepers have been notified and will review your report.
              You'll receive contact from a beekeeper if they're able to collect this swarm.
            </p>
            <p className="text-sm text-stone-500 mb-2">Reference: {reportId?.slice(0, 8)}</p>
            <Link to={`/swarm-status?id=${reportId}`} className="text-amber-600 underline text-sm block mb-6">Track your report status</Link>
            <Button onClick={() => { setSubmitted(false); setStep(1); setForm({ reporter_name: '', reporter_phone: '', reporter_email: '', address: '', suburb: '', latitude: 0, longitude: 0, situation_type: '', height_estimate: '', access_notes: '', already_arranged: false, last_seen: '', description: '' }); setPhotos([]); setPhotoPreview([]); }} variant="outline">
              Report Another Swarm
            </Button>
            <div className="flex justify-center gap-3 mt-6 pt-4 border-t border-stone-200">
              <Link to="/find-honey" className="text-amber-600 hover:text-amber-700 text-sm font-medium underline">
                Find Local Honey
              </Link>
              <span className="text-stone-300">|</span>
              <Link to="/advertise" className="text-amber-600 hover:text-amber-700 text-sm font-medium underline">
                Advertise With Us
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen honeycomb-bg">
      {/* Compact Nav Bar */}
      <header className="backdrop-blur-xl bg-white/80 border-b border-amber-200/60 sticky top-0 z-50">
        <div className="max-w-2xl mx-auto px-4 flex items-center justify-between h-14">
          <PublicLogo size="header" />
          <div className="flex gap-2">
            <Link to="/swarm-status" className="text-amber-700 hover:text-amber-800 text-xs font-medium px-3 py-1.5 rounded-full border border-amber-300 hover:bg-amber-50 transition-colors">
              Track My Report
            </Link>
          </div>
        </div>
      </header>

      <div className="max-w-2xl mx-auto p-4 space-y-5 pb-24">
        {/* Welcome Hero — shown on step 1 */}
        {step === 1 && (
          <>
            <div className="bg-gradient-to-br from-amber-500 to-amber-700 rounded-2xl p-6 text-white shadow-lg">
              <div className="flex items-center justify-center gap-5">
                <div className="text-center flex-1">
                  <h1 className="text-2xl font-extrabold">Found a Bee Swarm?</h1>
                  <p className="text-amber-100 mt-2 max-w-md mx-auto text-sm">
                    Don't worry — swarming bees are usually docile. We'll match you with a registered 
                    local beekeeper who can safely relocate them.
                  </p>
                </div>
                <PublicLogo size="hero" className="hidden sm:block flex-shrink-0 brightness-0 invert opacity-80" />
              </div>
              <div className="flex justify-center gap-4 mt-4">
                <div className="flex items-center gap-1.5 text-xs text-amber-100">
                  <span className="w-5 h-5 bg-white/20 rounded-full flex items-center justify-center text-white text-[10px] font-bold">1</span> Describe
                </div>
                <div className="flex items-center gap-1.5 text-xs text-amber-100">
                  <span className="w-5 h-5 bg-white/20 rounded-full flex items-center justify-center text-white text-[10px] font-bold">2</span> Location & Photos
                </div>
                <div className="flex items-center gap-1.5 text-xs text-amber-100">
                  <span className="w-5 h-5 bg-white/20 rounded-full flex items-center justify-center text-white text-[10px] font-bold">3</span> We Notify
                </div>
              </div>
            </div>
            {/* Free Collection Disclaimer */}
            <div className="bg-green-50 border border-green-300 rounded-xl p-3 flex items-start gap-3">
              <span className="text-xl flex-shrink-0">✅</span>
              <div>
                <p className="font-bold text-green-800 text-sm">Free Swarm Rescue Service</p>
                <p className="text-green-700 text-xs mt-0.5">This service connects you with volunteer beekeepers who collect swarms at no charge. 
                Paid removal services for bees in walls, roofs and chimneys are coming soon.</p>
              </div>
            </div>
          </>
        )}
        {/* Step indicators */}
        <div className="flex items-center justify-center gap-2 mb-4">
          {[1, 2, 3].map(s => (
            <button key={s} onClick={() => setStep(s)} className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold transition-colors ${step === s ? 'bg-amber-600 text-white' : step > s ? 'bg-green-500 text-white' : 'bg-stone-200 text-stone-500'}`}>
              {step > s ? '✓' : s}
            </button>
          ))}
        </div>

        {/* Step 1: What's happening */}
        {step === 1 && (
          <Card>
            <CardContent className="p-6">
              <h2 className="text-lg font-bold text-stone-800 mb-4">What's the situation?</h2>
              <div className="space-y-2">
                {SITUATION_OPTIONS.map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => update('situation_type', opt.value)}
                    data-testid={`situation-${opt.value}`}
                    className={`w-full text-left p-3 rounded-xl border-2 transition-colors flex items-center gap-3 ${
                      form.situation_type === opt.value ? 'border-amber-500 bg-amber-50' : 'border-stone-200 hover:border-amber-300'
                    }`}
                  >
                    <span className="text-xl">{opt.icon}</span>
                    <span className="font-medium text-stone-800">{opt.label}</span>
                    {!opt.free && <span className="ml-auto text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">Paid removal</span>}
                  </button>
                ))}
              </div>

              <div className="mt-6 space-y-4">
                <div>
                  <Label className="font-medium text-stone-700">How high up is it?</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {HEIGHT_OPTIONS.map(h => (
                      <button key={h} onClick={() => update('height_estimate', h)} className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${form.height_estimate === h ? 'bg-amber-500 text-white border-amber-500' : 'border-stone-300 hover:border-amber-400'}`}>
                        {h}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <Label className="font-medium text-stone-700">When was it last seen?</Label>
                  <Input value={form.last_seen} onChange={e => update('last_seen', e.target.value)} placeholder="e.g. Just now, 2 hours ago, Yesterday" className="mt-1" data-testid="last-seen-input" />
                </div>
              </div>

              <Button onClick={() => { if (!form.situation_type) return toast.error('Please select a situation'); setStep(2); }} className="w-full mt-6 bg-amber-600 hover:bg-amber-700 text-white" data-testid="step1-next">
                Next: Location & Photos
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Step 2: Location & Photos */}
        {step === 2 && (
          <Card>
            <CardContent className="p-6 space-y-6">
              <h2 className="text-lg font-bold text-stone-800 mb-2">Location & Photos</h2>

              {/* Location */}
              <div>
                <Label className="font-medium text-stone-700">Street Address</Label>
                <div className="flex gap-2 mt-1">
                  <AddressAutocomplete
                    value={form.address}
                    onChange={({ address, lat, lon, suburb, state, postcode }) => {
                      setForm(f => ({
                        ...f,
                        address: address || f.address,
                        latitude: lat || f.latitude,
                        longitude: lon || f.longitude,
                        suburb: suburb || f.suburb,
                        state: state || f.state,
                        postcode: postcode || f.postcode,
                      }));
                    }}
                    placeholder="Start typing street address..."
                    data-testid="address-input"
                  />
                  <Button type="button" onClick={useMyLocation} disabled={locating} variant="outline" className="flex-shrink-0">
                    {locating ? <Loader2 className="h-4 w-4 animate-spin" /> : <MapPin className="h-4 w-4" />}
                  </Button>
                </div>
                <div className="grid grid-cols-3 gap-2 mt-2">
                  <Input value={form.suburb} onChange={e => update('suburb', e.target.value)} placeholder="Suburb / City" className="col-span-1" data-testid="suburb-input" />
                  <Input value={form.state} onChange={e => update('state', e.target.value)} placeholder="State / Region" className="col-span-1" data-testid="state-input" />
                  <Input value={form.postcode} onChange={e => update('postcode', e.target.value)} placeholder="Postcode / ZIP" className="col-span-1" data-testid="postcode-input" />
                </div>
                {form.latitude ? (
                  <p className="text-xs text-green-600 mt-1 flex items-center gap-1"><CheckCircle className="h-3 w-3" /> Location confirmed ({form.latitude.toFixed(4)}, {form.longitude.toFixed(4)})</p>
                ) : form.address ? (
                  <p className="text-xs text-amber-600 mt-1">Select an address from the dropdown or use GPS for accurate location</p>
                ) : null}
              </div>

              {/* Photos */}
              <div>
                <Label className="font-medium text-stone-700">Photos (up to 5)</Label>
                <p className="text-sm text-stone-500 mb-2">Take or upload photos of the swarm — helps beekeepers assess the job</p>
                <div className="flex gap-2 flex-wrap">
                  {photoPreview.map((src, i) => (
                    <div key={i} className="relative w-20 h-20 rounded-lg overflow-hidden border-2 border-stone-200">
                      <img src={src} alt="" className="w-full h-full object-cover" />
                      <button onClick={() => removePhoto(i)} className="absolute top-0.5 right-0.5 bg-black/60 rounded-full p-0.5">
                        <X className="h-3 w-3 text-white" />
                      </button>
                    </div>
                  ))}
                  {photos.length < 5 && (
                    <button onClick={() => fileInputRef.current?.click()} className="w-20 h-20 rounded-lg border-2 border-dashed border-amber-400 flex flex-col items-center justify-center text-amber-600 hover:bg-amber-50 transition-colors" data-testid="add-photo-btn">
                      <Camera className="h-6 w-6" />
                      <span className="text-[10px] mt-0.5">Add</span>
                    </button>
                  )}
                </div>
                <input ref={fileInputRef} type="file" accept="image/*" capture="environment" multiple onChange={handlePhotos} className="hidden" />
              </div>

              {/* Access notes */}
              <div>
                <Label className="font-medium text-stone-700">Access Notes</Label>
                <textarea value={form.access_notes} onChange={e => update('access_notes', e.target.value)} placeholder="Gate code, parking, ladder needed, pet warnings, etc." className="w-full mt-1 p-3 border border-stone-300 rounded-lg text-sm min-h-[80px] focus:ring-2 focus:ring-amber-400 focus:border-amber-400" data-testid="access-notes" />
              </div>

              <div>
                <Label className="font-medium text-stone-700">Additional Description</Label>
                <textarea value={form.description} onChange={e => update('description', e.target.value)} placeholder="Anything else the beekeeper should know?" className="w-full mt-1 p-3 border border-stone-300 rounded-lg text-sm min-h-[60px] focus:ring-2 focus:ring-amber-400 focus:border-amber-400" />
              </div>

              <div className="flex gap-2">
                <Button onClick={() => setStep(1)} variant="outline" className="flex-1">Back</Button>
                <Button onClick={() => { if (!form.address && !form.latitude) return toast.error('Please provide a location'); setStep(3); }} className="flex-1 bg-amber-600 hover:bg-amber-700 text-white" data-testid="step2-next">
                  Next: Your Details
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 3: Contact Details & Submit */}
        {step === 3 && (
          <Card>
            <CardContent className="p-6 space-y-4">
              <h2 className="text-lg font-bold text-stone-800 mb-2">Your Contact Details</h2>
              <p className="text-sm text-stone-500">Your details will only be shared with the assigned beekeeper</p>

              <div>
                <Label className="font-medium text-stone-700">Your Name *</Label>
                <div className="relative mt-1">
                  <User className="absolute left-3 top-2.5 h-4 w-4 text-stone-400" />
                  <Input value={form.reporter_name} onChange={e => update('reporter_name', e.target.value)} placeholder="Full name" className="pl-10" data-testid="reporter-name" />
                </div>
              </div>

              <div>
                <Label className="font-medium text-stone-700">Phone Number *</Label>
                <div className="relative mt-1">
                  <Phone className="absolute left-3 top-2.5 h-4 w-4 text-stone-400" />
                  <Input value={form.reporter_phone} onChange={e => update('reporter_phone', e.target.value)} placeholder="Mobile number" type="tel" className="pl-10" data-testid="reporter-phone" />
                </div>
              </div>

              <div>
                <Label className="font-medium text-stone-700">Email (optional)</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-2.5 h-4 w-4 text-stone-400" />
                  <Input value={form.reporter_email} onChange={e => update('reporter_email', e.target.value)} placeholder="Email address" type="email" className="pl-10" data-testid="reporter-email" />
                </div>
              </div>

              {form.situation_type && !SITUATION_OPTIONS.find(o => o.value === form.situation_type)?.free && (
                <div className="p-3 border border-blue-200 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-800 font-medium">This looks like it may require a paid removal service</p>
                  <p className="text-xs text-blue-600 mt-1">A beekeeper will assess the job and discuss any costs with you before proceeding.</p>
                </div>
              )}

              <div className="flex gap-2 pt-2">
                <Button onClick={() => setStep(2)} variant="outline" className="flex-1">Back</Button>
                <Button onClick={handleSubmit} disabled={submitting} className="flex-1 bg-amber-600 hover:bg-amber-700 text-white" data-testid="submit-report-btn">
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                  {submitting ? 'Submitting...' : 'Submit Report'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

      </div>

      {/* Featured Removals Showcase */}
      <FeaturedRemovals />

      {/* Feature Nav */}
      <PublicFeatureNav current="/report-swarm" />
    </div>
  );
}


const SITUATION_LABELS = {
  bees_in_wall: 'Wall Cavity', bees_in_roof: 'Roof Space', bees_in_chimney: 'Chimney',
  established_colony: 'Established Colony', swarm_tree_branch: 'Tree Branch',
  swarm_ground_level: 'Ground Level', swarm_elevated: 'Elevated', swarm_fence_post: 'Fence/Post',
};

const GalleryPhoto = ({ path }) => {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    if (!path) return;
    fetch(`${API}/api/files/${path}`).then(r => r.blob()).then(b => setSrc(URL.createObjectURL(b))).catch(() => {});
  }, [path]);
  if (!src) return <div className="w-full h-full bg-stone-200 animate-pulse rounded-lg" />;
  return <img src={src} alt="Collection" className="w-full h-full object-cover rounded-lg" />;
};

function FeaturedRemovals() {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFeatured = async () => {
      try {
        // Get all public portfolios by fetching featured gallery photos
        const res = await fetch(`${API}/api/public/featured-removals`);
        if (res.ok) {
          const data = await res.json();
          setPhotos(data || []);
        }
      } catch {} finally { setLoading(false); }
    };
    fetchFeatured();
  }, []);

  if (loading || photos.length === 0) return null;

  // Group into before/after pairs where possible
  const pairs = [];
  const byReport = {};
  photos.forEach(p => {
    if (!byReport[p.report_id]) byReport[p.report_id] = [];
    byReport[p.report_id].push(p);
  });
  
  Object.entries(byReport).forEach(([reportId, reportPhotos]) => {
    const before = reportPhotos.find(p => p.type === 'before');
    const after = reportPhotos.find(p => p.type === 'after');
    const during = reportPhotos.find(p => p.type === 'during');
    if (before && after) {
      pairs.push({ before, after, during, suburb: before.suburb, type: before.situation_type });
    } else {
      // Show individual photos
      reportPhotos.slice(0, 2).forEach(p => {
        pairs.push({ single: p, suburb: p.suburb, type: p.situation_type });
      });
    }
  });

  if (pairs.length === 0) return null;

  return (
    <div className="max-w-lg mx-auto px-4 pb-8">
      <div className="text-center mb-5">
        <div className="flex items-center justify-center gap-2 mb-1">
          <Image className="h-5 w-5 text-amber-600" />
          <h2 className="text-lg font-bold text-stone-800">Featured Removals</h2>
        </div>
        <p className="text-sm text-stone-500">See the work of our local beekeepers</p>
      </div>

      <div className="space-y-4">
        {pairs.slice(0, 4).map((pair, idx) => (
          <div key={idx} className="bg-white rounded-xl border border-stone-200 overflow-hidden shadow-sm" data-testid={`featured-removal-${idx}`}>
            {pair.before && pair.after ? (
              <>
                <div className="grid grid-cols-2 gap-0.5 bg-stone-100">
                  <div className="relative aspect-[4/3]">
                    <GalleryPhoto path={pair.before.path} />
                    <span className="absolute top-2 left-2 bg-amber-500 text-white text-[9px] font-bold px-2 py-0.5 rounded-full shadow">Before</span>
                  </div>
                  <div className="relative aspect-[4/3]">
                    <GalleryPhoto path={pair.after.path} />
                    <span className="absolute top-2 left-2 bg-teal-500 text-white text-[9px] font-bold px-2 py-0.5 rounded-full shadow">After</span>
                  </div>
                </div>
                <div className="p-3 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-stone-800">{SITUATION_LABELS[pair.type] || 'Removal'}</p>
                    {pair.suburb && <p className="text-xs text-stone-500">{pair.suburb}</p>}
                  </div>
                  <span className="bg-teal-100 text-teal-700 text-[10px] font-bold px-2 py-0.5 rounded-full">Completed</span>
                </div>
              </>
            ) : pair.single ? (
              <>
                <div className="relative aspect-[16/9]">
                  <GalleryPhoto path={pair.single.path} />
                  <span className={`absolute top-2 left-2 text-white text-[9px] font-bold px-2 py-0.5 rounded-full shadow ${pair.single.type === 'before' ? 'bg-amber-500' : pair.single.type === 'during' ? 'bg-blue-500' : 'bg-teal-500'}`}>
                    {pair.single.type?.charAt(0).toUpperCase() + pair.single.type?.slice(1)}
                  </span>
                </div>
                <div className="p-3">
                  <p className="text-sm font-medium text-stone-800">{SITUATION_LABELS[pair.type] || 'Collection'}</p>
                  {pair.single.caption && <p className="text-xs text-stone-500 mt-0.5">{pair.single.caption}</p>}
                </div>
              </>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
}
